// B-47 — the words a person reads instead of a technical key, and the details behind a variable.
//
// The dictionary itself lives in the backend, one YAML per client (`mantis`,
// `panel/names/<client>.yaml`), and arrives with `getExtractionFields` as `names`:
//
//   { documents, sources, fields, calculations, alerts }   each  { <key>: { label, help } }
//
// This file only reads it. Nothing here names a client or a key (B-40's rule for this folder):
// a key the dictionary does not know falls back to `nombreLegibleDeClave`, which is what every
// screen showed before B-47 — never a blank.

import { moneda, nombreLegibleDeClave } from "./cierrePanel";
import { formatMoney } from "./formatMoney";

export const NOMBRES_VACIOS = Object.freeze({
  documents: {},
  sources: {},
  fields: {},
  calculations: {},
  alerts: {},
});

// A document or a source, by its key (`cuadre_oficina`, `informe_recaudo`).
export const nombreDeFuente = (nombres, clave) =>
  nombres?.documents?.[clave]?.label || nombres?.sources?.[clave]?.label || nombreLegibleDeClave(clave);

export const ayudaDeFuente = (nombres, clave) =>
  nombres?.documents?.[clave]?.help || nombres?.sources?.[clave]?.help || null;

// B-101 — several documents read from ONE file the office sends share its name before " · " in the
// client's names file ("Documentos del cierre · Arqueo", "… · Nota débito causal 55"): the screens
// show them as that one file, the name the person has on their disk.
const SEPARADOR_DE_ARCHIVO = " · ";

/**
 * A name and what it is also called, for the screens to draw the second smaller: "ARCHIVO TX BANCO
 * (conciliacion e pago)" -> { principal: "ARCHIVO TX BANCO", aparte: "conciliacion e pago" }.
 */
export const partesDeNombre = (nombre) => {
  const texto = String(nombre || "");
  const m = /^(.*\S)\s*\(([^()]+)\)\s*$/.exec(texto);
  return m ? { principal: m[1], aparte: m[2] } : { principal: texto, aparte: "" };
};

/** The file a document is read from, by the names file; null when it is a file of its own. */
export const archivoDeFuente = (nombres, clave) => {
  const nombre = nombreDeFuente(nombres, clave);
  const corte = nombre.indexOf(SEPARADOR_DE_ARCHIVO);
  return corte > 0 ? nombre.slice(0, corte) : null;
};

/** What a document is within its file ("Arqueo"), or its whole name. */
export const parteDeFuente = (nombres, clave) => {
  const nombre = nombreDeFuente(nombres, clave);
  const corte = nombre.indexOf(SEPARADOR_DE_ARCHIVO);
  return corte > 0 ? nombre.slice(corte + SEPARADOR_DE_ARCHIVO.length) : nombre;
};

/** One name for documents that travel together: their file's, when they all share one. */
export const nombreDeDocumentos = (nombres, claves) => {
  const archivos = [...new Set(claves.map((clave) => archivoDeFuente(nombres, clave)))];
  if (claves.length > 1 && archivos.length === 1 && archivos[0]) return archivos[0];
  return claves.map((clave) => nombreDeFuente(nombres, clave)).join(" + ");
};

/**
 * Documents grouped by the file they come in, in their first one's order: `[{ nombre, claves }]`.
 * A document of its own is a group of one, named as it always was.
 */
export const agruparPorArchivo = (nombres, claves) => {
  const grupos = [];
  claves.forEach((clave) => {
    const archivo = archivoDeFuente(nombres, clave);
    const grupo = archivo ? grupos.find((g) => g.archivo === archivo) : null;
    if (grupo) grupo.claves.push(clave);
    else grupos.push({ archivo, nombre: archivo || nombreDeFuente(nombres, clave), claves: [clave] });
  });
  return grupos.map(({ nombre, claves: deGrupo }) => ({ nombre, claves: deGrupo }));
};

// A datapoint, by its `<source>.<field>` key. The fallback prettifies only the field half: the
// source is shown separately in the details, and "Informe Recaudo Subtotales Valor Total" was
// exactly the kind of label this task exists to remove.
export const nombreDeDato = (nombres, ref) => {
  const entrada = nombres?.fields?.[ref];
  if (entrada) return entrada.label;
  const punto = String(ref || "").indexOf(".");
  return nombreLegibleDeClave(punto > 0 ? ref.slice(punto + 1) : ref);
};

export const ayudaDeDato = (nombres, ref) => nombres?.fields?.[ref]?.help || null;

export const nombreDeCalculo = (nombres, clave) =>
  nombres?.calculations?.[clave]?.label || nombreLegibleDeClave(clave);

export const ayudaDeCalculo = (nombres, clave) => nombres?.calculations?.[clave]?.help || null;

export const nombreDeAlerta = (nombres, clave) =>
  nombres?.alerts?.[clave]?.label || nombreLegibleDeClave(clave);

export const ayudaDeAlerta = (nombres, clave) => nombres?.alerts?.[clave]?.help || null;

// How a value came to be, in the operator's words. `origin` is `closing_datapoints.origin`.
const ORIGENES = {
  EXTRACTION: "Leído del documento",
  MODEL: "Leído del documento",
  HUMAN: "Digitado por una persona",
  API: "Recibido por integración",
  DERIVED: "Calculado",
};

export const origenLegible = (origin) => ORIGENES[origin] || (origin ? String(origin) : null);

// A stored value as text — money formatted, a date as printed, an absent value as a dash. The
// `type` is the declaration's when the caller knows it; without it a numeric value is shown as
// a plain number, never guessed into pesos.
export const valorLegible = (valor, tipo) => {
  if (valor === null || valor === undefined || valor === "") return "—";
  if (tipo === "money") return Number.isFinite(Number(valor)) ? moneda(valor) : String(valor);
  if (tipo === "count" || tipo === "number") {
    const numero = Number(valor);
    if (!Number.isFinite(numero)) return String(valor);
    // Thousands grouped, no currency sign: a calculation's input may be pesos or a count, and
    // "$20" for twenty transactions would be a wrong statement, not a formatting slip.
    return `${numero < 0 ? "-" : ""}${formatMoney(Math.abs(Math.trunc(numero)))}`;
  }
  return String(valor);
};

const fechaHora = (iso) => {
  if (!iso) return null;
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return null;
  return fecha.toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short", timeZone: "America/Bogota" });
};

// The rows of a datapoint's details panel: what it is, where it came from, how and when.
//
// `vivo` is the closing detail's `datapoints[].live` row (value, origin, recordedBy, reason,
// recordedAt, sourceId). `archivo` is the file name of the document it was read from, when the
// caller could find one. Rows with nothing to say are left out rather than shown empty.
export const detallesDeDato = ({ nombres, ref, vivo, archivo }) => {
  const punto = String(ref || "").indexOf(".");
  const fuente = punto > 0 ? ref.slice(0, punto) : null;
  const filas = [
    { etiqueta: "Qué es", valor: ayudaDeDato(nombres, ref) },
    { etiqueta: "De dónde sale", valor: fuente ? nombreDeFuente(nombres, fuente) : null },
    { etiqueta: "Archivo", valor: archivo || null },
    { etiqueta: "Cómo se obtuvo", valor: vivo ? origenLegible(vivo.origin) : "Todavía no hay valor" },
    {
      etiqueta: "Registrado",
      valor: vivo?.recordedAt
        ? [fechaHora(vivo.recordedAt), vivo.origin === "HUMAN" && vivo.recordedBy ? `por ${vivo.recordedBy}` : null]
          .filter(Boolean)
          .join(" ")
        : null,
    },
    { etiqueta: "Motivo", valor: vivo?.origin === "HUMAN" ? vivo.reason || null : null },
  ];
  return filas.filter((fila) => fila.valor);
};

// The rows of a calculation's details panel: what it checks, and the values it was computed from.
//
// `resultado` is a `toResultView` row plus the detail's `inputs` (`{ ref, kind, value }`, from
// `inspect/model.js#describeInput`). An unresolved calculation lists what it is missing instead.
export const detallesDeCalculo = ({ nombres, resultado, entradas }) => {
  const filas = [{ etiqueta: "Qué revisa", valor: ayudaDeCalculo(nombres, resultado?.key) }];

  const usadas = (entradas || []).filter((e) => e?.ref);
  if (usadas.length) {
    filas.push({
      etiqueta: "Se calcula con",
      valor: usadas
        .map((e) => {
          const nombre = e.kind === "calculation" ? nombreDeCalculo(nombres, e.ref) : nombreDeDato(nombres, e.ref);
          const valor = e.value === null || e.value === undefined ? null : valorLegible(e.value, "number");
          return valor ? `${nombre}: ${valor}` : nombre;
        })
        .join(" · "),
    });
  }

  const faltan = (resultado?.missingInputs || []).filter(Boolean);
  if (!resultado?.resolved && faltan.length) {
    filas.push({
      etiqueta: "Falta",
      valor: faltan
        .map((ref) => (String(ref).includes(".") ? nombreDeDato(nombres, ref) : nombreDeCalculo(nombres, ref)))
        .join(", "),
    });
  }

  return filas.filter((fila) => fila.valor);
};

// A declared field type, in words. The worksheet used to print the type itself ("money").
const TIPOS = {
  money: "Valor en pesos",
  count: "Cantidad (número entero)",
  date: "Fecha",
  text: "Texto",
  enum: "Una opción de una lista",
};

export const tipoLegible = (tipo) => TIPOS[tipo] || null;

// The details a worksheet field shows behind "Ver detalles": what it is, where it is printed in
// the document (the declaration's own `locate:`), and what kind of value it takes.
export const detallesDeCampoManual = ({ nombres, field }) => [
  { etiqueta: "Qué es", valor: ayudaDeDato(nombres, field?.datapointKey) },
  { etiqueta: "Dónde está en el documento", valor: field?.locate || null },
  { etiqueta: "Tipo de dato", valor: tipoLegible(field?.type) },
].filter((fila) => fila.valor);

// A datapoint's label read under its source's name: "Total del día (Excel de recaudos)" ->
// "Total del día" when the sentence already says "en Excel de recaudos del mes".
const sinSufijoDeFuente = (etiqueta) => etiqueta.replace(/\s*\([^)]*\)\s*$/, "") || etiqueta;

const listaEnPalabras = (items) => (items.length <= 1
  ? items.join("")
  : `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`);

// What is missing, in words, grouped by where it comes from. The same sentence the backend
// writes for a blocked alert since B-47 (`panel/names.js#missingSentence`) — kept identical so
// an alert raised before that change reads the same as one raised after.
export const frasesDeFaltantes = (nombres, refs) => {
  const porFuente = new Map();
  const calculos = [];
  (refs || []).forEach((ref) => {
    const punto = String(ref).indexOf(".");
    if (punto <= 0) {
      calculos.push(nombreDeCalculo(nombres, ref));
      return;
    }
    const fuente = ref.slice(0, punto);
    const etiqueta = nombres?.fields?.[ref]?.label;
    if (!porFuente.has(fuente)) porFuente.set(fuente, []);
    porFuente.get(fuente).push(etiqueta ? sinSufijoDeFuente(etiqueta) : nombreDeDato(nombres, ref));
  });
  const partes = [...porFuente.entries()].map(
    ([fuente, campos]) => `Falta en ${nombreDeFuente(nombres, fuente)}: ${listaEnPalabras([...new Set(campos)])}.`
  );
  if (calculos.length) partes.push(`Falta el resultado: ${listaEnPalabras([...new Set(calculos)])}.`);
  return partes;
};

// An alert's message, readable. A blocked alert raised before B-47 carries the keys
// ('No se pudo evaluar la alerta "x": falta a.b, c.d'); it is rewritten here the way the backend
// now writes it. Every other message is the backend's own sentence and is returned untouched.
const BLOQUEADA_ANTIGUA = /^No se pudo evaluar la alerta "[^"]*": falta (.+)$/;

export const mensajeDeAlerta = (nombres, mensaje) => {
  const coincide = BLOQUEADA_ANTIGUA.exec(String(mensaje || ""));
  if (!coincide) return mensaje || "";
  const refs = coincide[1].split(",").map((r) => r.trim()).filter(Boolean);
  return ["No se pudo revisar todavía.", ...frasesDeFaltantes(nombres, refs)].join(" ");
};
